/**
 * Per-system world deltas (M3 spec §9; plan R5): depleted rocks, looted POIs, cleared bases and M2's opaque wrecks,
 * plus discovered POIs. Deltas are created lazily by the writers; readers get a shared empty delta.
 */
import type { ReachState, SystemDelta } from '../types'

export interface ReadonlySystemDelta {
  readonly rocks: Readonly<Record<string, number>>
  readonly lootedPois: readonly string[]
  readonly clearedBases: readonly string[]
  readonly wrecks: readonly unknown[]
}

const EMPTY_DELTA: ReadonlySystemDelta = Object.freeze({
  rocks: Object.freeze({}),
  lootedPois: Object.freeze([]),
  clearedBases: Object.freeze([]),
  wrecks: Object.freeze([]),
})

/** The system's delta, or a shared frozen empty one when nothing was written (never creates). Allocation-free. */
export function deltaOf(state: ReachState, systemId: string): ReadonlySystemDelta {
  return state.deltas[systemId] ?? EMPTY_DELTA
}

/** The stored delta, created on first write. */
export function ensureDelta(state: ReachState, systemId: string): SystemDelta {
  let d = state.deltas[systemId]
  if (!d) {
    d = { rocks: {}, lootedPois: [], clearedBases: [], wrecks: [] }
    state.deltas[systemId] = d
  }
  return d
}

/**
 * Records a rock's remaining fraction, clamped to 0..1 (0 = gone). A fraction of 1 (or more) removes the entry, so
 * untouched rocks cost nothing in the save. True when the stored value changed.
 */
export function setRockRemaining(state: ReachState, systemId: string, rockKey: string, fraction: number): boolean {
  const f = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0
  const current = state.deltas[systemId]?.rocks[rockKey] ?? 1
  if (current === f) return false
  if (f === 1) {
    const d = state.deltas[systemId]
    if (d) Reflect.deleteProperty(d.rocks, rockKey)
    return true
  }
  ensureDelta(state, systemId).rocks[rockKey] = f
  return true
}

/** Allocation-free: the rock's remaining fraction (1 when untouched). */
export function rockRemaining(state: ReachState, systemId: string, rockKey: string): number {
  return state.deltas[systemId]?.rocks[rockKey] ?? 1
}

function addOnce(list: string[], id: string): boolean {
  if (list.includes(id)) return false
  list.push(id)
  return true
}

/** True the first time. */
export function markPoiLooted(state: ReachState, systemId: string, poiId: string): boolean {
  return addOnce(ensureDelta(state, systemId).lootedPois, poiId)
}

/** True the first time. */
export function markBaseCleared(state: ReachState, systemId: string, poiId: string): boolean {
  return addOnce(ensureDelta(state, systemId).clearedBases, poiId)
}

/** True the first time a (hidden) POI is discovered. */
export function discoverPoi(state: ReachState, systemId: string, poiId: string): boolean {
  let list = state.discoveredPois[systemId]
  if (list?.includes(poiId)) return false
  if (!list) {
    list = []
    state.discoveredPois[systemId] = list
  }
  list.push(poiId)
  return true
}

/** Allocation-free. */
export function isPoiDiscovered(state: ReachState, systemId: string, poiId: string): boolean {
  const list = state.discoveredPois[systemId]
  return list !== undefined && list.includes(poiId)
}
